<template>
  <div
    class="checkout-container"
    :style="{
      'background-image': `url(${require('@/assets/lw-order-process-background-min.jpg')})`,
    }"
  >
    <template v-if="!loading">
      <ProgressBar :steps="checkoutSteps" :current-step="currentCheckoutStep" />

      <div class="checkout">
        <div class="checkout-form">
          <router-view :key="routePath" />
        </div>
        <router-view
          name="summary"
          :key="routePath"
          :nickname="planNickname"
          :package="selectedPackage"
          :app="selectedApp"
          :location="selectedLocation"
          :term="packageTerm"
          :loading="loadingSummary"
          :storebuilder="storebuilder"
        />
      </div>
    </template>

    <div class="loader center-middle" v-if="loading">
      <LoadingState :size="50" />
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import constants from "@/utils/constants";
import LoadingState from "@/components/display/LoadingState";
import ProgressBar from "@/components/display/ProgressBar";
import store from "@/store";

export default {
  name: "CheckoutTrialPage",
  components: {
    LoadingState,
    ProgressBar,
  },
  props: {
    appId: Number,
    packageId: Number,
    locationId: Number,
    term: String,
    couponCode: String,
    nickname: String,
    installApp: Boolean,
    affiliateId: Number,
    addonId: Number,
    hasBigCommerce: Boolean,
    storeBuilderId: String,
  },
  setup(props, { root }) {
    const requireStoreBuilder = computed(() => store.getters["checkout/requireStoreBuilder"]);
    const routePath = computed(() => root.$route.fullPath);
    const checkoutSteps = computed(() => {
      if (requireStoreBuilder.value) {
        return constants.CHECKOUT_TRIAL_STOREBUILDER_STEPS;
      }
      return constants.CHECKOUT_TRIAL_STEPS;
    });
    const currentCheckoutStep = computed(() => root.$route.name);
    const selectedApp = computed(() =>
      store.getters["apps/getAppById"](store.state.checkout.appId)
    );
    const selectedPackage = computed(() =>
      store.getters["appPackages/getAppPackage"](
        store.state.checkout.packageId,
        store.state.checkout.appId
      )
    );
    const selectedLocation = computed(() =>
      store.getters["locations/getLocationById"](store.state.checkout.locationId)
    );
    const planNickname = computed(() => store.state.checkout.nickname);
    const packageTerm = computed(() => store.state.checkout.term);
    const loadingSummary = computed(() => !selectedPackage.value || !selectedApp.value);

    // when first mounted, loading state to grab user info
    const loading = ref(true);

    onMounted(() => {
      store.dispatch("checkout/cartstackActive", true);
      store
        .dispatch("settings/fetch")
        .then(() => store.dispatch("apps/getList"))
        .then(() => store.dispatch("appPackages/getList"))
        .then(() => store.dispatch("locations/getList"))
        .finally(() => {
          store.dispatch("checkout/setApp", props.appId);
          store.dispatch("checkout/setPackage", props.packageId);
          store.dispatch("checkout/setLocation", props.locationId);
          store.dispatch("checkout/setTerm", props.term);
          store.dispatch("checkout/setAffiliate", props.affiliateId);
          store.dispatch("checkout/setInstallApp", props.installApp);
          store.dispatch("checkout/setCouponCode", props.couponCode);
          store.dispatch("checkout/addAddon", props.addonId);
          store.dispatch("checkout/setHasBigCommerce", props.hasBigCommerce);
          store.dispatch("checkout/setStoreBuilderId", props.storeBuilderId);
          store.dispatch(
            "checkout/setNickname",
            `${root.$t(`global.${selectedApp.value.type}`)} ${root.$t("global.trial")}`
          );

          loading.value = false;
        });
    });

    const storebuilder = computed(() => store.getters["checkout/requireStoreBuilderUi"]);

    return {
      loading,
      routePath,
      checkoutSteps,
      currentCheckoutStep,
      selectedPackage,
      selectedApp,
      selectedLocation,
      packageTerm,
      planNickname,
      loadingSummary,
      storebuilder,
    };
  },
};
</script>
<style lang="scss">
@import "~@/styles/variables";

.checkout-container {
  background-size: cover;
  height: 100%;
  overflow: auto;
  padding-top: 1.5rem;
}
</style>
